import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowRightIcon, Loader2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button as UiKitButton } from "@/components/ui-kit/button"
import { useContract } from "@/hooks/contracts/useContracts"
import { usePinCodes } from "@/hooks/guard/usePinCodes"
import { useFacilities } from "@/hooks/warehouse/useWarehouse"
import { cn } from "@/lib/utils"
import type { Facility } from "@/types/warehouse"

function getFacilityProperty(facility: Facility | undefined, code: string) {
  return facility?.properties?.find((property) => property.code === code)?.value
}

type InfoBlockProps = {
  badge: string
  value: string
  actionLabel: string
  onAction?: () => void
  actionDisabled?: boolean
  className?: string
}

function InfoBlock({
  badge,
  value,
  actionLabel,
  onAction,
  actionDisabled = false,
  className,
}: InfoBlockProps) {
  return (
    <div className={cn("flex flex-col sm:rounded-[4px] rounded-[8px] bg-background sm:p-4 p-3 min-h-[140px]", className)}>
      <Badge className="h-auto rounded-[2px] bg-white px-1 py-0.5 text-[12px] leading-4 font-medium text-light-muted mb-4">
        {badge}
      </Badge>
      <p className="sm:text-[18px] text-[24px] leading-[110%] sm:leading-[24px] text-foreground mt-auto mb-2 font-semibold">{value}</p>
      <UiKitButton
        type="button"
        variant="link"
        className="h-auto w-fit justify-start p-0 text-[18px] leading-[24px] text-brand"
        disabled={actionDisabled}
        onClick={onAction}
      >
        {actionLabel}
      </UiKitButton>
    </div>
  )
}

export function PaymentDoneLeftPanel({ contractId }: { contractId: number }) {
  const navigate = useNavigate()

  const { data: contract, isLoading: isContractLoading } = useContract(contractId)
  const { data: facilities, isLoading: isFacilitiesLoading } = useFacilities()
  const { data: pinCodes, isLoading: isPinCodesLoading } = usePinCodes()

  const facility = useMemo(() => {
    if (!contract || !facilities?.items) {
      return undefined
    }

    return facilities.items.find((item) => item.facility.code === contract.facility)?.facility
  }, [contract, facilities])

  const pinCode = pinCodes?.items?.find((item) => item.facilityCode === contract?.facility)

  const facilityAddress =
    getFacilityProperty(facility, "address") ??
    getFacilityProperty(facility, "display_name") ??
    facility?.name ??
    "—"

  const unitCode = contract?.unitItem?.code ?? contract?.unit ?? "—"
  const pin = pinCode?.pin ?? "—"

  const isLoading = isContractLoading || isFacilitiesLoading || isPinCodesLoading


  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 md:h-full md:py-0">
        <Loader2 className="size-8 animate-spin text-brand" />
      </div>
    )
  }

  return (
    <div className="flex flex-col sm:p-8 px-2 py-6 md:h-full md:min-h-0 md:overflow-y-auto">
      <div className="sm:mb-[32px] mb-6">
        <h1 className="mb-3 sm:text-[56px] text-[32px] leading-[36px] sm:leading-[64px] font-semibold text-foreground">
          Вы оплатили бокс!
        </h1>
        <p className="sm:text-[18px] text-[16px] leading-[20px] sm:leading-[24px] text-muted-foreground">
          На ваш номер прислали смс-сообщение с пин-кодом, а на электронную почту договор
          оказания услуг.
        </p>
      </div>

      <div className="sm:mb-6 mb-4 flex flex-col gap-2">
        <InfoBlock
          badge="Адрес склада"
          value={facilityAddress}
          actionLabel="Схема проезда"
          onAction={() => {}}
        />

        <div className="flex flex-col sm:flex-row gap-2">
          <InfoBlock
            className="min-w-0 flex-1"
            badge="Бокс"
            value={unitCode}
            actionLabel="Инструкция доступа"
            onAction={() => {}}
          />
          <InfoBlock
            className="min-w-0 flex-1"
            badge="Пин-код"
            value={pin}
            actionLabel="Зачем он нужен?"
            onAction={() => {}}
          />
        </div>
      </div>

      <div className="sm:mb-6 mb-4 space-y-[12px] text-[16px] sm:text-[18px] leading-[110%] sm:leading-[24px] text-foreground">
        <p>Склад доступен 24/7</p>
        <p>
          Для разгрузки или погрузки в плохую погоду <br /> воспользуйтесь крытой парковкой.
        </p>
      </div>

      <div className="pt-6 md:mt-auto hidden sm:block">
        <p className="mb-3 text-sm leading-5 text-muted-foreground">
          Управляйте подпиской на боксы в своем личном кабинете
        </p>
        <UiKitButton type="button" onClick={() => navigate("/home")} endIcon={<ArrowRightIcon />}>
          В личный кабинет
        </UiKitButton>
      </div>
    </div>
  )
}
